import {
    format,
    isSameDay,
    secondsToMilliseconds,
    secondsToMinutes,
    subDays,
} from 'date-fns';
import { TFunction } from 'i18next';

import { EPGProgram } from 'custom_modules/react-native-epg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import { PROGRAM_ICON_TYPE_ID, PROGRAM_LIST_ITEM_ID } from 'config/testID';

import { EpgProgram } from 'store/railsBuilder/reducer.types';
import { CachedRecordingItem } from 'store/recordings/reducer.types';

// import { makeTestDeeplinkTitle } from 'utils/getMockDeeplink';
import parseParentalRating from 'services/data-manager/parsing/utils/parseParentalRating';

import { EpgProgramExtra, EpgProgramIcon } from '../types';

type Params = {
    t: TFunction;
    program: EpgProgram;
    subscribed: boolean;
    selectedDate: number;
    startoverAllowed: boolean;
    catchupAllowed: boolean;
    tvChannel?: number;
    recordingItem: CachedRecordingItem | undefined;
    shouldHideShortDuration?: boolean;
};

/*
  EPG Cell texts and icons display clauses.

  0-5 - ''
  6-10 - t('EPG_PROGRAM_TITLE_SHORT')
  11-29 - title... + time...
  30-N - title(...) + icon
 */
export enum DURATION_LIMITATIONS {
    EMPTY = 5,
    SHORT = 10,
    TRUNCATED = 30,
}

function programCellPropsAreEqual(
    prevProgram: EPGProgram<EpgProgramExtra> | undefined,
    nextProgram: EPGProgram<EpgProgramExtra> | undefined,
) {
    return (
        prevProgram?.extra?.recordingItem?.status ===
        nextProgram?.extra?.recordingItem?.status
    );
}

export default function parseRBProgramToEpgProgram({
    t,
    program,
    subscribed,
    selectedDate,
    startoverAllowed,
    catchupAllowed,
    tvChannel,
    recordingItem,
    shouldHideShortDuration = true,
}: Params): EPGProgram<EpgProgramExtra> {
    const {
        id,
        title,
        period: { start, end },
    } = program;
    const startTime = secondsToMilliseconds(start);
    const endTime = secondsToMilliseconds(end);
    const durationInMin = secondsToMinutes(end - start);

    const testID =
        `${PROGRAM_LIST_ITEM_ID}${program.title}-${tvChannel}`.replace(
            / /g,
            '',
        );
    const iconTestID =
        durationInMin >= DURATION_LIMITATIONS.TRUNCATED
            ? `${PROGRAM_ICON_TYPE_ID}_${getProgramIconType({
                  subscribed,
                  startoverAllowed,
                  catchupAllowed,
              })}`
            : '';

    return {
        id,
        channelId: program.serviceRef,
        // TODO Remove DL from the title for release build(s).
        // title: makeTestDeeplinkTitle(program, title),
        title: getTitleByDuration(t, title, durationInMin),
        startTime,
        endTime,
        extra: {
            testID,
            iconTestID,
            subscribed,
            recordingItem,
            propsAreEqual: programCellPropsAreEqual,
            minimizedCell: durationInMin <= DURATION_LIMITATIONS.SHORT,
            time: getTimeByDuration(
                startTime,
                endTime,
                durationInMin,
                shouldHideShortDuration,
            ),
            isFirstCell:
                isSameDay(startTime, subDays(endTime, 1)) &&
                isSameDay(endTime, selectedDate),
            isLastCell:
                isSameDay(startTime, subDays(endTime, 1)) &&
                isSameDay(startTime, selectedDate),
            iconType:
                durationInMin >= DURATION_LIMITATIONS.TRUNCATED
                    ? getProgramIconType({
                          subscribed,
                          startoverAllowed,
                          catchupAllowed,
                      })
                    : undefined,
            rating: parseParentalRating(program),
            originalTitle: title,
        },
    };
}

function getTitleByDuration(
    t: TFunction,
    title: string | undefined,
    duration: number,
) {
    if (
        duration > DURATION_LIMITATIONS.EMPTY &&
        duration <= DURATION_LIMITATIONS.SHORT
    ) {
        return LANGUAGE_KEYS.EPG.EPG_PROGRAM_TITLE_SHORT;
    }

    if (duration <= DURATION_LIMITATIONS.EMPTY) {
        return '';
    }

    return title?.trim() || '';
}

function getTimeByDuration(
    startTime: number,
    endTime: number,
    duration: number,
    shouldHideShortDuration: boolean,
) {
    return duration <= DURATION_LIMITATIONS.SHORT && shouldHideShortDuration
        ? ''
        : `${format(startTime, 'HH:mm')} - ${format(endTime, 'HH:mm')}`;
}

function getProgramIconType({
    subscribed,
    startoverAllowed,
    catchupAllowed,
}: {
    subscribed: boolean;
    startoverAllowed: boolean;
    catchupAllowed: boolean;
}) {
    // TODO Clauses for Locked content icon and Parental rating icon
    //      should be added based on the icon prio when Parental Rating
    //      taks will be implemented.
    // Prio is: 1 - Locked adult content, 2 - Parental rating, 3 - Not subscribed, 4 - Replay
    if (!subscribed) {
        return EpgProgramIcon.NOT_SUBSCRIBED;
    }

    if (catchupAllowed) {
        return EpgProgramIcon.REPLAY_CU;
    }

    if (startoverAllowed) {
        return EpgProgramIcon.REPLAY_SO;
    }

    return undefined;
}
